import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/outline';
import * as XLSX from 'xlsx';
import axiosInstance from '../../axios';
import FinancialYear from '../Dropdowns/FinancialYear';
import { toast } from 'react-toastify';
const formatCurrency = (value) => {
  if (isNaN(value)) return value; // In case value is not a number, return it as-is
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(value);
};

const LedgerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { centre_id } = location.state || {};
  const [ledgers, setLedgers] = useState([]);
  const [tBalance, setTbalance] = useState('');
  const [selectedYear, setSelectedYear] = useState("");
  const [centreName, setCentreName] = useState('');
  const [editRowId, setEditRowId] = useState(null);
  const [editedLedger, setEditedLedger] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;
  // Calculate the total number of pages


  const sortLedgersByDate = (ledgers) => {
    return [...ledgers].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
  };

  useEffect(() => {
    const fetchLedgers = async () => {
      const response = await axiosInstance.post(`/ledger/getRecords`, { centre_id });
      const records = response.data.individualRecords;
      //console.log(response.data.individualRecords)

      const updatedRecords = records.map(ledger => ({
        ...ledger,
      }));
      // updatedRecords = sortLedgersByDate(updatedRecords);
      // console.log(updatedRecords)
      setLedgers(updatedRecords);
      setTbalance(response.data.aggregateData.Balance);
      setCentreName(updatedRecords.length > 0 ? updatedRecords[0].centre_name : '');
    };
    fetchLedgers();
  }, [centre_id]);

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const exportToExcel = () => {
    if (filteredLedgers.length === 0) {
      toast.error("No data found for the selected year");
      return;
    }

    const filteredLedgersForExport = filteredLedgers.map((ledger) => ({
      Head: ledger.head,
      Date: ledger.date,
      Payable: ledger.payble,
      Paid: ledger.paid,
      Expenses: ledger.expenses,
      Remark: ledger.remark,
      Balance: ledger.balance
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredLedgersForExport);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ledgers');

    XLSX.writeFile(workbook, 'ledger-details.xlsx');
  };

  const handleEditClick = (ledger) => {
    setEditRowId(ledger.ledger_id);
    setEditedLedger(ledger);
  };

  const recalculateBalances = (updatedLedger) => {
    const initialBalance = (parseFloat(updatedLedger.payble) || 0) - (parseFloat(updatedLedger.paid) || 0) - (parseFloat(updatedLedger.expenses) || 0);

    let updatedLedgers = ledgers.map((ledger) => {
      if (ledger.ledger_id === updatedLedger.ledger_id) {
        return { ...updatedLedger, balance: isNaN(initialBalance) ? 0 : initialBalance };
      }
      return ledger;
    });

    updatedLedgers = sortLedgersByDate(updatedLedgers);

    let firstBalance = 0
    for (let i = 0; i < updatedLedgers.length; i++) {
      const currentPayable = parseFloat(updatedLedgers[i].payble) || 0;
      const currentPaid = parseFloat(updatedLedgers[i].paid) || 0;
      const currentExpenses = parseFloat(updatedLedgers[i].expenses) || 0;

      let previousBalance = 0;
      if (i > 0) {
        previousBalance = updatedLedgers[i - 1].balance;
      }
      const currentBalance = (previousBalance + currentPayable) - currentPaid - currentExpenses;

      updatedLedgers[i].balance = isNaN(currentBalance) ? 0 : currentBalance;
      // console.log("First:",updatedLedgers[i].balance)

      if (i == updatedLedgers.findIndex((l) => l.ledger_id === updatedLedger.ledger_id)) {
        editedLedger.balance = updatedLedgers[i].balance;
        firstBalance = updatedLedgers[i].balance;
      }
    }

    setLedgers(updatedLedgers);
    return firstBalance;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === 'date' ? value : parseFloat(value);
    const updatedLedger = {
      ...editedLedger,
      [name]: name === 'date' ? value : isNaN(parsedValue) ? value : parsedValue, // Handle date and numeric values
    };
    //console.log(editedLedger.date.toString().substring(0, 10))
    //console.log("updatedLedger", updatedLedger);

    updatedLedger.balance = recalculateBalances(updatedLedger);
    setEditedLedger(updatedLedger);
    // console.log("second:",editedLedger.balance);
  };

  const handleSaveClick = async (ledgerId) => {
    try {
      // Check if the current edited ledger has valid values
      if (isNaN(parseFloat(editedLedger.balance))) {
        console.error("Balance cannot be NaN");
        return; // Prevent the update if balance is NaN
      }

      // Find the index of the edited ledger
      // const editedLedgerIndex = ledgers.findIndex((ledger) => ledger.ledger_id === ledgerId);

      // Add the edited ledger to the array of updates
      // let updatedLedgers = [...ledgers];
      // updatedLedgers[editedLedgerIndex] = { ...updatedLedgers[editedLedgerIndex], ...editedLedger };

      // Recalculate balances for the edited ledger and the ones that follow
      // recalculateBalances(editedLedger);

      // Create an array of ledgers that have been edited (including the current one)
      const editedLedgers = ledgers.map(ledger => ({
        id: ledger.ledger_id,
        payble: ledger.payble,
        head: ledger.head,
        paid: ledger.paid,
        expenses: ledger.expenses,
        remark: ledger.remark,
        date: ledger.date.toString().substring(0, 10),
        balance: ledger.balance
      }));
      // console.log(editedLedgers)
      // Send API request to update all the edited ledgers in a single request
      await axiosInstance.put('/ledger/update', editedLedgers);

      // console.log('Ledgers updated successfully:', editedLedgers);
      toast.success('Ledgers updated successfully')
      // Exit edit mode
      setEditRowId(null);
      setEditedLedger({});
    } catch (error) {
      console.log(error)
      toast.error('Error updating the ledgers');
    }
  };

  const handleCancelClick = () => {
    setEditRowId(null);
    setEditedLedger({});
  };

  const filteredLedgers = selectedYear
    ? ledgers.filter((ledger) => new Date(ledger.date).getFullYear().toString() === selectedYear)
    : ledgers;

  const formatDateToDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, '0'); // Add leading zero if needed
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    //console.log(date)
    return `${day}-${month}-${year}`;
  }
  const totalPages = Math.ceil(filteredLedgers.length / entriesPerPage);

  // Get the data for the current page
  const currentTableData = filteredLedgers.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleText = () => {
    setIsExpanded(!isExpanded);
  };
  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center px-2 ">
      <div className="flex flex-col gap-5 w-full h-full max-w-6xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <ArrowLeftIcon
              className="w-6 h-6 text-gray-600 cursor-pointer hover:text-gray-800 transition"
              onClick={() => navigate('/ledger')}
            />
            <h1 className="md:text-2xl font-semibold text-md text-gray-700">
              Centre Name: {centreName}
            </h1>
          </div>
          <button
            onClick={exportToExcel}
            className="bg-blue-600 text-white md:px-4 md:py-2 px-1 py-2 rounded text-sm md:text-md hover:bg-blue-700 transition"
          >
            Export to Excel
          </button>
        </div>
        <div className='flex justify-center min-h-full'>
          <FinancialYear onChange={handleYearChange} selectedFinancialYear={selectedYear} />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white h-full border border-t-gray-200 text-sm md:text-base" style={{tableLayout:"fixed"}}>
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="p-4 text-left text-gray-800 font-semibold">Sr.No</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Head</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Date</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Payable</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Paid</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Expenses</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Remark</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Balance</th>
                <th className="p-4 text-left text-gray-800 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentTableData.length > 0 ? (
                currentTableData.map((ledger,index) => (
                  <tr key={ledger.ledger_id} className="border-b border-gray-200">
                    <td className="p-4 text-gray-600">{index + 1}</td>
                    <td className="p-4 text-gray-600" style={{width: "60px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <input
                          type="text"
                          name="head"
                          value={editedLedger.head}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        ledger.head
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "120px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <input
                          type="date"
                          name="date"
                          value={new Date(editedLedger.date).toISOString().split('T')[0]}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        formatDateToDDMMYYYY(new Date(ledger.date))
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "120px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <input
                          type="number"
                          name="payble"
                          value={editedLedger.payble}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        formatCurrency(ledger.payble)
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "120px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <input
                          type="number"
                          name="paid"
                          value={editedLedger.paid}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        formatCurrency(ledger.paid)
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "120px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <input
                          type="number"
                          name="expenses"
                          value={editedLedger.expenses}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        formatCurrency(ledger.expenses)
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "200px"}}>
                      {editRowId === ledger.ledger_id ? (
                        <textarea
                          type="text"
                          name="remark"
                          value={editedLedger.remark}
                          onChange={handleInputChange}
                          className="border border-gray-300 rounded p-1"
                        />
                      ) : (
                        ledger.remark
                      )}
                    </td>
                    <td className="p-4 text-gray-600" style={{width: "120px"}}>
                      {formatCurrency(ledger.balance)}
                    </td>
                    <td className="p-4 text-gray-600 flex space-x-2 min-w-full">
                      {editRowId === ledger.ledger_id ? (
                        <div className='flex flex-col gap-5'>
                          <button
                            onClick={() => handleSaveClick(ledger.ledger_id)}
                            className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelClick}
                            className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(ledger)}
                          className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-4 text-center text-gray-600">
                    No ledgers found for the selected year.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!editRowId &&
         (
            <div className="flex justify-between items-center bg-gray-100">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-300 text-purple-700 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-300 text-blue-700 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LedgerDetails;
